import { ChevronRightIcon, WrenchIcon } from 'lucide-react'
import { memo, type ReactNode, useState } from 'react'

import { cn } from '@/lib/utils'

import { ChatAgentItemLayout } from './chat-agent-item-layout.atom'
import type { ChatToolCall, ToolCallClickHandler } from './chat-message.interface'
import { ChatToolCallBadge } from './chat-tool-call-badge.atom'

export interface ChatToolCallItemProps {
  toolCall: ChatToolCall
  agentName?: string
  isPinned?: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: ToolCallClickHandler
  className?: string
}

const toFriendlyName = (slug: string): string =>
  slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

const buildToolLogoUrl = (toolName: string): string | null => {
  const cdnUrl = import.meta.env.VITE_CDN_URL
  if (!cdnUrl) return null
  const slug = toolName.toLowerCase().split('_')[0]
  return `${cdnUrl}/tools-logos/${slug}-logo.png`
}

interface ToolCallIconBoxProps {
  isError: boolean
  toolName: string
}

const ToolCallIconBox: React.FC<ToolCallIconBoxProps> = ({ isError, toolName }): ReactNode => {
  const [hasImageError, setHasImageError] = useState(false)
  const logoUrl = buildToolLogoUrl(toolName)
  const showImage = logoUrl && !hasImageError

  return (
    <div
      className={cn(
        'flex size-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md',
        isError ? 'bg-destructive/10' : 'bg-primary/10'
      )}
    >
      {showImage ? (
        <img
          src={logoUrl}
          alt={toolName}
          className="size-6 object-contain"
          onError={() => setHasImageError(true)}
        />
      ) : (
        <WrenchIcon className={cn('size-4', isError ? 'text-destructive' : 'text-primary')} />
      )}
    </div>
  )
}

const ChatToolCallItemBase: React.FC<ChatToolCallItemProps> = ({
  toolCall,
  agentName,
  isPinned = false,
  isHighlighted = false,
  onPin,
  onClick,
  className,
}): ReactNode => {
  const { toolName, friendlyName, status, progressMessage } = toolCall
  const isError = status === 'error'
  const displayName = friendlyName ?? toFriendlyName(toolName)
  const handleClick = onClick ? () => onClick(toolCall) : undefined
  const subtitleText = progressMessage ?? toolName

  return (
    <ChatAgentItemLayout
      itemId={toolCall.id}
      timestamp={toolCall.timestamp}
      agentName={agentName}
      isPinned={isPinned}
      isHighlighted={isHighlighted}
      onPin={onPin}
      onClick={handleClick}
      wrapperClassName="chat-tool-call"
      cardClassName={cn(
        'chat-tool-call-card flex-col gap-2',
        isError && 'border-destructive/30 bg-destructive/5'
      )}
      className={className}
    >
      <div className="flex items-center gap-3">
        <ToolCallIconBox isError={isError} toolName={toolName} />
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">
            {displayName}
          </span>
          <ChatToolCallBadge status={status} />
        </div>
        {onClick && (
          <ChevronRightIcon className="size-4 flex-shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground" />
        )}
      </div>
      <span className="truncate text-left text-xs text-muted-foreground">{subtitleText}</span>
    </ChatAgentItemLayout>
  )
}

export const ChatToolCallItem = memo(ChatToolCallItemBase)

ChatToolCallItem.displayName = 'ChatToolCallItem'
